import { checkPermissions } from '@sharedInterfaces/IPermissions';
import getCertificate from '@src/APIs/graphQl/Certificate/getCertificate';
import CertificatePage from '@src/App/Pages/EntityPages/Certificates/CertificatePage/CertificatePage';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import Overlay from '../Overlay/Overlay';
import './CertificateOverlay.css';

interface CertificateOverlayProps {
    certificateId: number;
    children: React.ReactNode;
    disabled?: boolean;
    open?: boolean;
    onClose?: () => void;
}

/**
 * Represents a Certificate Overlay component.
 *
 * @param {CertificateOverlayProps} props - The props for the Certificate Overlay component.
 * @returns {JSX.Element} The rendered Certificate Overlay component.
 */
function CertificateOverlay(props: CertificateOverlayProps) {
    const entity = useSelector((state: AppState) => state.certificates.find((a) => a.id === props.certificateId));
    const permissions = useSelector((state: AppState) => state.permissions);
    const isAllowed =
        checkPermissions('Certificates', 'Retrieve', permissions) ||
        checkPermissions('Certificates', 'RetrieveOwn', permissions);
    const disabled = !isAllowed || props.disabled;
    const [error, setError] = React.useState<Error | null>(null);

    return (
        <Overlay
            disabled={disabled}
            triggerItem={props.children}
            loaded={entity !== null}
            error={error}
            open={props.open}
            onClose={props.onClose}
            onFirstOpen={() => {
                getCertificate(props.certificateId).catch(setError);
            }}
        >
            {entity && <CertificatePage entity={entity} inDialog={true} />}
        </Overlay>
    );
}

export default CertificateOverlay;
